import { BodyText, Card, HeaderText } from "@/components/ui";
import { programTrackDisplayName } from "@/constants/AccessProgram";
import { Fragment } from "react";
import type { UseFormReturn } from "react-hook-form";
import { deriveEnrollmentIcd10Codes } from "../ProgramEnrollmentStep/programEnrollment";
import { POST_ENCOUNTER_ACTIVITY_LABELS } from "../postEncounterActivities";
import type { VisitClosureFormData, VisitClosureFormInput } from "../schemas";
import {
  BILL_TYPE_LABELS,
  PATIENT_TYPE_LABELS,
  VISIT_TYPE_LABELS,
} from "../visitDurationOptions";

interface BillingSummaryStepProps {
  form: UseFormReturn<VisitClosureFormInput, unknown, VisitClosureFormData>;
}

export const BillingSummaryStep = ({ form }: BillingSummaryStepProps) => {
  const chartingTimeSpent = form.watch("encounterTimeData.chartingTimeSpent");
  const othersChartingTimeSpent = form.watch(
    "encounterTimeData.othersChartingTimeSpent",
  );
  let totalEncounterTime = chartingTimeSpent + othersChartingTimeSpent;

  const postEncounterActivities = form.watch(
    "encounterTimeData.postEncounterActivities",
  );

  const { primaryIcd10Code, additionalIcd10Codes } = deriveEnrollmentIcd10Codes(
    form.watch(),
  );

  const enrollmentApproved = form.watch("programEnrollment.enrollmentApproved");
  const encounterProgram = form.watch("programEnrollment.encounterProgram");

  const patientType = form.watch("encounterTimeData.patientType");
  const billType = form.watch("encounterTimeData.billType");
  const visitType = form.watch("encounterTimeData.visitType");

  const cptCode = form.watch("cptCode.coding.0.code");
  const cptCodeDisplay = form.watch("cptCode.text");

  for (const activity of postEncounterActivities) {
    totalEncounterTime += activity.time;
  }

  return (
    <Card className="p-4 flex w-full flex-col gap-3">
      <div className="flex flex-col gap-1">
        <HeaderText size="sm" tag="h3">
          Primary ICD-10 Code
        </HeaderText>
        <BodyText size="sm" textColor="secondary">
          {primaryIcd10Code.code
            ? `${primaryIcd10Code.code} - ${primaryIcd10Code.display}`
            : "UNKNOWN"}
        </BodyText>
      </div>
      {additionalIcd10Codes.length > 0 && (
        <div className="flex flex-col gap-1">
          <HeaderText size="sm" tag="h3">
            Additional ICD-10 Codes
          </HeaderText>
          {additionalIcd10Codes.map(({ code, sourceKey }) => (
            <BodyText size="sm" textColor="secondary" key={sourceKey}>
              {code.code} - {code.display}
            </BodyText>
          ))}
        </div>
      )}
      {encounterProgram && typeof enrollmentApproved === "boolean" && (
        <div className="flex flex-col gap-1">
          <HeaderText size="sm" tag="h3">
            ACCESS Enrollment
          </HeaderText>
          <BodyText size="sm" textColor="secondary">
            {enrollmentApproved ? "Approved" : "Not approved"} for{" "}
            {programTrackDisplayName(encounterProgram.type)} ACCESS program
          </BodyText>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <HeaderText size="sm" tag="h3">
          Encounter Details
        </HeaderText>
        <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[max-content_minmax(0,1fr)]">
          <BodyText size="sm" textColor="secondary">
            Clinician Time:
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            {chartingTimeSpent.toLocaleString()} minutes
          </BodyText>
          {othersChartingTimeSpent > 0 && (
            <>
              <BodyText size="sm" textColor="secondary">
                Other Billable Clinical Time:
              </BodyText>
              <BodyText size="sm" textColor="secondary">
                {othersChartingTimeSpent} minutes
              </BodyText>
            </>
          )}
          {postEncounterActivities.map((activity) => {
            if (activity.time <= 0) {
              return null;
            }

            return (
              <Fragment key={activity.activityType}>
                <BodyText size="sm" textColor="secondary">
                  {POST_ENCOUNTER_ACTIVITY_LABELS[activity.activityType]}
                </BodyText>
                <BodyText size="sm" textColor="secondary">
                  {activity.time} minutes
                </BodyText>
              </Fragment>
            );
          })}
          <BodyText size="sm" textColor="secondary">
            Patient Type:
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            {patientType ? PATIENT_TYPE_LABELS[patientType] : ""}
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            Bill Type:
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            {BILL_TYPE_LABELS[billType]}
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            Visit Type:
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            {VISIT_TYPE_LABELS[visitType]}
          </BodyText>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <HeaderText size="sm" tag="h3">
          CPT Code
        </HeaderText>
        <BodyText size="sm" textColor="secondary">
          {cptCode || "UNKNOWN"} - {cptCodeDisplay}
        </BodyText>
      </div>
      <div className="flex flex-col gap-1 self-end">
        <HeaderText size="sm" tag="h3">
          Attestation
        </HeaderText>
        <BodyText size="sm" textColor="primary">
          The total duration of this encounter was {totalEncounterTime}{" "}
          minute(s). By clicking submit, you attest to the above time spent and
          you are providing an alternative electronic signature.
        </BodyText>
        <BodyText size="xs" textColor="secondary" className="mt-2">
          CPT ©️ 2023 American Medical Association. All rights reserved.
        </BodyText>
      </div>
    </Card>
  );
};
